// Story 5.3 + 5.7 — Rejection reason dialog (shared between Admin and Finance)
import { useState } from 'react';
import { XCircle } from 'lucide-react';

const PREDEFINED_REASONS = [
  'Missing receipts',
  'Incorrect amounts',
  'Insufficient documentation',
  'Policy violation',
  'Other',
] as const;

// Matches the API's rejection reason limit (z.string().max(1000))
const MAX_REASON_LENGTH = 1000;

interface RejectionDialogProps {
  title?: string;
  isLoading: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function RejectionDialog({
  title = 'Reject Claim',
  isLoading,
  onConfirm,
  onCancel,
}: RejectionDialogProps) {
  const [selected, setSelected] = useState<string>('');
  const [custom, setCustom] = useState('');

  const isOther = selected === 'Other';
  const detail = custom.trim();
  // Preset: "<preset>: <detail>" when detail given, else the preset alone. Other: detail only.
  const reason = isOther ? detail : detail ? `${selected}: ${detail}` : selected;
  const tooLong = reason.length > MAX_REASON_LENGTH;
  const canSubmit = reason.length > 0 && !tooLong && !isLoading;

  const handleSubmit = () => {
    if (canSubmit) onConfirm(reason);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm cursor-default"
        onClick={onCancel}
      />
      <div className="relative bg-surface rounded-2xl border border-border shadow-2xl p-6 w-full max-w-md space-y-5">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-soft text-danger">
            <XCircle className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-text">{title}</h2>
            <p className="text-sm text-text-2 mt-0.5">
              Select a reason. The claimant will see this message.
            </p>
          </div>
        </div>

        {/* Predefined reasons */}
        <div className="space-y-2">
          {PREDEFINED_REASONS.map((r) => (
            <button
              key={r}
              id={`reject-reason-${r.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setSelected(r)}
              className={`w-full text-left px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                selected === r
                  ? 'border-danger bg-danger-soft text-danger'
                  : 'border-border bg-surface text-text hover:bg-surface-2'
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        {/* Details (optional for presets, required for "Other") */}
        {selected && (
          <div className="space-y-1">
            <label htmlFor="rejection-custom-reason" className="text-xs font-medium text-text-2">
              {isOther ? 'Explain the rejection reason' : 'Additional details (optional)'}
            </label>
            <textarea
              id="rejection-custom-reason"
              className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-2 focus:outline-2 focus:ring-destructive/40 resize-none"
              rows={3}
              placeholder={isOther ? 'Describe the issue...' : 'Add specifics to help the claimant fix this...'}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              autoFocus
            />
            {tooLong && (
              <p className="text-xs text-danger">
                Reason is too long ({reason.length}/{MAX_REASON_LENGTH} characters).
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <button
            id="rejection-cancel-btn"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-medium text-text hover:bg-surface-2 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            id="rejection-confirm-btn"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 px-4 py-2.5 rounded-xl bg-danger text-white text-sm font-medium hover:bg-danger/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <span className="w-3.5 h-3.5 rounded-full border-2 border-danger-foreground border-t-transparent animate-spin" />
                Rejecting…
              </>
            ) : (
              'Confirm Rejection'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
